import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChefHat, Trophy, Flame, Heart, Crown, BadgeCheck, Clock, Users,
  PlusCircle, Sparkles, ArrowLeft, ArrowRight, Play,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MEDAL = ["🥇", "🥈", "🥉"];

const timeLeft = (endsAt) => {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return "terminé";
  const days  = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days > 0)  return `${days}j ${hours}h`;
  const min = Math.floor((ms % 3600000) / 60000);
  return `${hours}h ${min}min`;
};

export const ChallengePage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [payload, setPayload] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get(`/challenges/${slug}`).then(({ data }) => setPayload(data)).catch(() => setPayload(null));
  };
  useEffect(load, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (payload === null) {
    return <div className="px-6 py-14 max-w-5xl mx-auto text-center text-white/60">Chargement…</div>;
  }

  const { challenge, entries, my_votes = [], my_entries = [], eligible = [] } = payload;
  const ended = new Date(challenge.ends_at).getTime() < Date.now();

  const toggleVote = async (trackId) => {
    if (!user) { toast.error("Connecte-toi pour voter"); return; }
    const alreadyVoted = my_votes.includes(trackId);
    try {
      await api({
        method: alreadyVoted ? "DELETE" : "POST",
        url: `/challenges/${slug}/vote/${trackId}`,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur vote");
    }
  };

  const submitTrack = async (trackId) => {
    setSubmitting(true);
    try {
      await api.post(`/challenges/${slug}/entries`, { track_id: trackId });
      toast.success("Ta recette est en lice ! 🔥");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-6xl mx-auto pb-24" data-testid="challenge-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white mb-6">
        <ArrowLeft size={12} /> Retour à l'accueil
      </Link>

      {/* HERO */}
      <div
        className="relative rounded-3xl border border-brand-red/30 overflow-hidden p-8 md:p-12 mb-10"
        style={{
          backgroundImage: challenge.banner_url
            ? `linear-gradient(135deg, rgba(227,27,35,0.65), rgba(10,6,5,0.92)), url(${challenge.banner_url})`
            : "linear-gradient(135deg, rgba(227,27,35,0.25), rgba(249,87,56,0.1))",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="challenge-hero"
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="tag-pill bg-brand-red/20 border-brand-red/40 text-brand-red inline-flex items-center gap-1.5">
            <ChefHat size={12} /> Défi cuisine du mois
          </span>
          <span className={`inline-flex items-center gap-1 tag-pill text-[10px] ${ended ? "bg-white/10 text-white/60" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"}`}>
            <Clock size={10} /> {ended ? "Terminé" : `Plus que ${timeLeft(challenge.ends_at)}`}
          </span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-3 text-balance">
          <span className="mr-3">{challenge.emoji}</span>
          {challenge.dish_name}
        </h1>
        {challenge.theme && (
          <p className="text-xl text-white/80 mb-4 font-semibold">{challenge.theme}</p>
        )}
        {challenge.description && (
          <p className="text-white/70 max-w-2xl mb-6">{challenge.description}</p>
        )}

        <div className="flex flex-wrap gap-3 text-sm">
          <Stat label="Participations" value={payload.entries_count} icon={Users} />
          <Stat label="Votes" value={entries.reduce((s, e) => s + Number(e.votes || 0), 0)} icon={Heart} />
          <Stat label="Top 3 en vedette" value="Chef du mois" icon={Crown} />
        </div>
      </div>

      {/* Eligible tracks / submit CTA */}
      {!ended && user && (
        <div className="rounded-2xl border border-brand-yellow/30 bg-brand-yellow/5 p-5 mb-8" data-testid="challenge-submit-block">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-brand-yellow" />
            <h3 className="font-display text-lg font-black">Participer au défi</h3>
          </div>
          {eligible.length > 0 ? (
            <>
              <p className="text-sm text-white/70 mb-3">
                Tes recettes publiées qui peuvent rejoindre le défi :
              </p>
              <ul className="space-y-1.5">
                {eligible.map((t) => (
                  <li key={t.track_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.03] border border-white/10">
                    <ChefHat size={14} className="text-brand-orange" />
                    <span className="flex-1 font-semibold truncate">{t.title}</span>
                    <button
                      onClick={() => submitTrack(t.track_id)}
                      disabled={submitting}
                      className="btn-primary text-xs inline-flex items-center gap-1 px-3 py-1.5 disabled:opacity-60"
                      data-testid={`submit-track-${t.track_id}`}
                    >
                      <PlusCircle size={12} /> Soumettre
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-white/70">
              Publie d'abord une vidéo / photo dans la catégorie <strong className="text-brand-orange">Cuisine & recettes</strong> pour participer.{" "}
              <Link to="/upload" className="text-brand-red hover:underline inline-flex items-center gap-1">
                Publier maintenant <ArrowRight size={12} />
              </Link>
            </p>
          )}
          {my_entries.length > 0 && (
            <p className="text-xs text-emerald-400 mt-3">
              ✓ Tu as déjà {my_entries.length} recette{my_entries.length > 1 ? "s" : ""} en lice. Bonne chance !
            </p>
          )}
        </div>
      )}

      {/* LEADERBOARD */}
      <div className="flex items-center gap-2 mb-5">
        <Trophy size={18} className="text-brand-yellow" />
        <h2 className="font-display text-2xl md:text-3xl font-black">Classement</h2>
        <span className="text-sm text-white/50 ml-auto">{entries.length} participation{entries.length > 1 ? "s" : ""}</span>
      </div>

      {entries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-white/50">
          <Flame className="mx-auto text-white/30 mb-3" />
          Sois le premier à soumettre ta version ! 🍲
        </div>
      ) : (
        <ol className="space-y-2" data-testid="challenge-leaderboard">
          {entries.map((e, i) => {
            const isTop3 = i < 3;
            const iVoted = my_votes.includes(e.track_id);
            const isMine = e.user_id === user?.user_id;
            return (
              <li
                key={e.entry_id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                  isTop3 ? "border-brand-yellow/30 bg-brand-yellow/5" : "border-white/10 bg-white/[0.02] hover:border-white/25"
                }`}
                data-testid={`entry-${e.track_id}`}
              >
                <span className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-lg font-black ${
                  isTop3 ? "bg-brand-yellow/20 text-brand-yellow" : "bg-white/5 text-white/50"
                }`}>
                  {MEDAL[i] || `#${i + 1}`}
                </span>

                {e.cover_path ? (
                  <img src={e.cover_path} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center shrink-0">
                    <ChefHat size={18} />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <Link to={`/track/${e.track_id}`} className="font-semibold hover:text-brand-orange truncate block" data-testid={`entry-title-${e.track_id}`}>
                    {e.title}
                  </Link>
                  <p className="text-xs text-white/50 truncate">
                    <Link to={`/u/${e.user_id}`} className="hover:text-white inline-flex items-center gap-1">
                      {e.artist_name}
                      {Number(e.is_verified) === 1 && <BadgeCheck size={10} className="text-brand-yellow" />}
                    </Link>
                    {e.city && ` · ${e.city}`}
                    {isMine && <span className="ml-1 text-brand-orange">· toi</span>}
                  </p>
                </div>

                {e.external_url && (
                  <a
                    href={e.external_url}
                    target="_blank"
                    rel="noreferrer"
                    className="hidden sm:inline-flex text-white/40 hover:text-brand-orange p-1"
                    aria-label="Voir la recette"
                  >
                    <Play size={14} />
                  </a>
                )}

                <button
                  onClick={() => toggleVote(e.track_id)}
                  disabled={ended || isMine}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    iVoted
                      ? "bg-brand-red/20 text-brand-red border-brand-red/40"
                      : "bg-white/[0.03] text-white/70 border-white/10 hover:border-brand-red/40 hover:text-brand-red"
                  }`}
                  title={isMine ? "Tu ne peux pas voter pour toi-même" : ended ? "Défi terminé" : (iVoted ? "Retirer mon vote" : "Voter")}
                  data-testid={`vote-${e.track_id}`}
                >
                  <Heart size={13} className={iVoted ? "fill-current" : ""} />
                  {e.votes}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

const Stat = ({ label, value, icon: Icon }) => (
  <div className="inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-md border border-white/10 px-4 py-1.5">
    <Icon size={13} className="text-brand-yellow" />
    <span className="font-bold text-white">{value}</span>
    <span className="text-[11px] uppercase tracking-wider text-white/60">{label}</span>
  </div>
);

export default ChallengePage;
